"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.remove = exports.findBySolution = exports.mergeSameSolutionInstallables = exports.mergeInstallables = exports.filterInstalledWithin = exports.sumSubsidisableProductAmounts = exports.isSubsidizableItem = exports.isSubsidizableProduct = void 0;
const sum_1 = require("../utils/sum");
/** Returns true if subsidy can be requested for the installation of this product, i.e. the product has an ISDE code */
function isSubsidizableProduct(product) {
    return Boolean(product.isdeCode);
}
exports.isSubsidizableProduct = isSubsidizableProduct;
/** Returns true if subsidy can be requested for the installation of the product on this item, i.e. `item.product` has an ISDE code */
function isSubsidizableItem(item) {
    return isSubsidizableProduct(item.product);
}
exports.isSubsidizableItem = isSubsidizableItem;
/** Returns the total amount (sum of item.amount) of subsidisable products in the list of installable items */
function sumSubsidisableProductAmounts(items) {
    return (0, sum_1.sum)(items.map(item => (isSubsidizableProduct(item.product) ? item.amount : 0)));
}
exports.sumSubsidisableProductAmounts = sumSubsidisableProductAmounts;
/**
 * Returns from the list of installables the installables that either do not have an installation date,
 * or the installation date is on or after `range.start` and, if defined, before `range.end`.
 */
function filterInstalledWithin(installables, range) {
    return installables.filter(installable => !installable.installationDate ||
        (installable.installationDate >= range.start &&
            (!range.end || installable.installationDate < range.end)));
}
exports.filterInstalledWithin = filterInstalledWithin;
/**
 * Merges a list of installables. Will use the solution and installation date from the first installable in the list.
 * Throws an error if the list is empty.
 */
function mergeInstallables(installables) {
    if (!installables.length)
        throw new Error('cannot merge an empty list of installables');
    if (installables.length === 1)
        return installables[0];
    return {
        solution: installables[0].solution,
        items: installables.flatMap(i => i.items),
        installationDate: installables[0].installationDate,
    };
}
exports.mergeInstallables = mergeInstallables;
/**
 * Given a list of installables where there might be multiple installables with the same solution,
 * returns a list with installables with only one installable of each solution. Thus, every solution
 * in returned list only occurs once.
 *
 * When there exist multiple installables with the same solution, they will be merged into one installable of that solution.
 */
function mergeSameSolutionInstallables(installables) {
    // group installables by solution
    const bySolution = new Map();
    for (const installable of installables) {
        const solutionInstallables = bySolution.get(installable.solution) || [];
        bySolution.set(installable.solution, [...solutionInstallables, installable]);
    }
    // if there are multiple installables for the same solution, merge them.
    return [...bySolution.entries()].map(([_, solutionInstallables]) => {
        if (solutionInstallables.length === 1)
            return solutionInstallables[0];
        return mergeInstallables(solutionInstallables);
    });
}
exports.mergeSameSolutionInstallables = mergeSameSolutionInstallables;
/**
 * Finds the first installable with the given solution and returns it in an array
 * where the first element is the found installable (or undefined if not found)
 * and the second element is a function to replace or remove that element in the `installables` array.
 */
function findBySolution(installables, solution) {
    const index = installables.findIndex(i => i.solution === solution);
    const replace = (replacement) => {
        if (replacement)
            installables.splice(index, 1, replacement);
        else
            installables.splice(index, 1);
    };
    return [installables[index], replace];
}
exports.findBySolution = findBySolution;
function remove(array, elementOrPredicate) {
    if (typeof elementOrPredicate === 'function') {
        const predicate = elementOrPredicate;
        array.splice(array.findIndex(predicate), 1);
        return;
    }
    // indexOf does not work on objects, so we handle finding objects by JSON.stringify equality.
    if (typeof elementOrPredicate === 'object') {
        const elementJSON = JSON.stringify(elementOrPredicate);
        array.splice(array.findIndex(value => JSON.stringify(value) === elementJSON));
        return;
    }
    const element = elementOrPredicate;
    array.splice(array.indexOf(element), 1);
}
exports.remove = remove;
